import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { BUILT_IN_LEVELS, builtInLevelForTable } from '../engine/tables/builtInLevels';
import type { TableDefinition } from '../engine/tables/definition';
import type { RenderMode } from '../engine/types';
import { preload3DExperience } from '../render3d/Table3DShowroom';
import TablePreview from './TablePreview';
import { BRAND_SPRITES } from '../assets/brandSprites';
import { preloadTableExperience } from '../assets/tablePreload';
import { themeStagePresentation } from '../assets/themePresentation';
import SelectedTableScores from '../community/SelectedTableScores';
import { parseScoreChallenge } from '../community/share';
import { homeHeroPlayBlocked } from './homeHeroReadiness';
import { scheduleHomepageWarmup } from './homeHeroWarmup';
import { loadLocalScores } from './localLeaderboard';
import styles from './HomeHero.module.css';
import loadingStyles from './TableLoadingScreen.module.css';

const MARQUEE_MARKS: Record<string, string> = {
  'classic-zero': '●',
  'grand-circuit-v1': '▰',
  'candy-carnival': '✦',
  'cosmic-relay': '✧',
  'haunted-keep': '◇',
  'neon-rush': '⌁',
  'jungle-temple': '◆',
  'deep-sea-dive': '≋',
  'clockwork-district': '⚙',
  'frontier-run': '✶',
  'dragon-keep': '▲',
};

export default function HomeHero({
  table,
  loading,
  playable,
  renderMode,
  effectsEnabled,
  attractMotion,
  afterHero,
  onSelect,
  onPlay,
  onRemix,
  onNewTable,
}: {
  table: TableDefinition;
  loading: boolean;
  playable: boolean;
  renderMode: RenderMode;
  effectsEnabled: boolean;
  attractMotion: boolean;
  afterHero?: ReactNode;
  onSelect: (table: TableDefinition) => void;
  onPlay: () => void;
  onRemix: () => void;
  onNewTable: () => void;
}) {
  const heroRef = useRef<HTMLElement>(null);
  const [heroVisible, setHeroVisible] = useState(true);
  const [documentVisible, setDocumentVisible] = useState(() => document.visibilityState !== 'hidden');

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroVisible(Boolean(entry?.isIntersecting)),
      { rootMargin: '160px 0px' },
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => setDocumentVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    return scheduleHomepageWarmup(() => {
      void preloadTableExperience(table).catch(() => {
        // Runtime rendering/audio already have fail-open paths and may retry.
      });
    }, window);
  }, [table]);

  const official = builtInLevelForTable(table);
  const selectedIndex = Math.max(0, BUILT_IN_LEVELS.findIndex((level) => level.table.id === table.id));
  const selectRelative = (direction: -1 | 1) => {
    const next = (selectedIndex + direction + BUILT_IN_LEVELS.length) % BUILT_IN_LEVELS.length;
    onSelect(BUILT_IN_LEVELS[next]!.table);
  };
  const accent = table.theme.accentColor ?? '#f0b44d';
  const secondary = table.theme.secondaryColor ?? '#79a9c9';
  const animateAttract = attractMotion && heroVisible && documentVisible;
  const playBlocked = homeHeroPlayBlocked({ loading, playable });
  const onlineScores = Boolean(official || table.provenance?.isPublished);
  const challenge = typeof window === 'undefined' ? null : parseScoreChallenge(window.location.search);
  const activeChallenge = challenge?.tableId === table.id ? challenge : null;
  const localScores = loadLocalScores();
  const presentation = themeStagePresentation(table,false);
  const marqueeTheme = table.theme.machineArtId ?? table.id;
  const marqueeMark = MARQUEE_MARKS[marqueeTheme] ?? '●';
  const sceneStyle: CSSProperties = {
    backgroundColor: presentation.backgroundColor,
    backgroundImage: presentation.backgroundImage,
    backgroundSize: presentation.backgroundSize,
    backgroundPosition: presentation.backgroundPosition,
  };
  const prewarmPlayIntent = () => {
    if (renderMode === '3d' && !playBlocked) preload3DExperience();
  };

  return (
    <section
      ref={heroRef}
      className={styles.hero}
      style={{ '--hero-accent': accent, '--hero-secondary': secondary } as CSSProperties}
      aria-label="Balls of Pin homepage"
    >

      <div className={`${styles.stage} ${animateAttract ? '' : styles.still}`}>
        <div className={styles.sceneArt} style={sceneStyle} aria-hidden="true" />
        <div className={styles.sceneTint} />
        {loading && (
          <div className={loadingStyles.screen} role="status" aria-live="polite" aria-label="Loading selected table">
            <div className={loadingStyles.card}>
              <img src={BRAND_SPRITES.logo} alt="" aria-hidden="true" />
              <div className={loadingStyles.spinner} aria-hidden="true" />
            </div>
          </div>
        )}
        <button className={`${styles.arrow} ${styles.left}`} onClick={() => selectRelative(-1)} aria-label="Previous official table">‹</button>
        <button className={`${styles.arrow} ${styles.right}`} onClick={() => selectRelative(1)} aria-label="Next official table">›</button>

        <div className={styles.marquee} data-theme={marqueeTheme} aria-label={table.name}>
          <span className={styles.marqueeMark} aria-hidden="true">{marqueeMark}</span>
          <div><strong>{table.name}</strong>{official && <small>{official.difficulty}</small>}</div>
          <span className={styles.marqueeLine} aria-hidden="true" />
        </div>

        <div className={styles.machineCard}>
          {effectsEnabled && <div className={styles.previewGlow} />}
          <TablePreview table={table} className={styles.preview} />
          {effectsEnabled && <div className={styles.attractLights} aria-hidden="true"><i /><i /><i /><i /><i /></div>}
        </div>

        <div className={styles.actions}>
          <button
            className={styles.play}
            onClick={onPlay}
            onPointerEnter={prewarmPlayIntent}
            onPointerDown={prewarmPlayIntent}
            onFocus={prewarmPlayIntent}
            disabled={playBlocked}
            aria-busy={loading}
            aria-label="Play table"
            title="Play"
          >
            Play
          </button>
          <button className={styles.remix} onClick={onNewTable}>New table</button>
          <button className={styles.remix} onClick={onRemix}>{table.provenance?.isPublished ? 'Remix' : 'Edit'}</button>
        </div>
      </div>

      <nav className={styles.dots} aria-label="Official tables">
        {BUILT_IN_LEVELS.map((level) => <button key={level.id} className={level.table.id === table.id ? styles.activeDot : ''} onClick={() => onSelect(level.table)} aria-label={`Select ${level.name}`} title={level.name} />)}
      </nav>

      <section className={styles.controlsSection} aria-label="Default keyboard controls">
        <div className={styles.controlGroup} aria-label="Left flipper"><kbd>Z</kbd><kbd>←</kbd></div>
        <span className={styles.controlBall} aria-hidden="true">●</span>
        <div className={styles.controlGroup} aria-label="Right flipper"><kbd>→</kbd><kbd>/</kbd></div>
        <div className={styles.controlGroup} aria-label="Launch"><kbd>↓</kbd><kbd className={styles.spaceKey}>SPACE</kbd></div>
        <div className={styles.controlGroup} aria-label="Nudge"><kbd>A</kbd><kbd>W</kbd><kbd>D</kbd></div>
      </section>

      <div id="community-table-slot" className={styles.communitySlot}>
        <SelectedTableScores
          key={table.id}
          tableId={table.id}
          tableName={table.name}
          localScores={localScores}
          online={onlineScores}
          challenge={activeChallenge}
        />
        {afterHero}
      </div>

      <footer className={styles.legal}>
        <nav aria-label="Legal and privacy"><a href="/privacy">Privacy</a><a href="/terms">Terms</a></nav>
      </footer>
    </section>
  );
}
